import {
  AD_GROUP_NAME_MAX_LENGTH,
  CreateAdGroupSchema,
  PricingRuleCommonInputSchema,
} from '@hprofits/api/ad-group/schemas';
import type { CreatePricingRuleInput } from '@hprofits/api-client';
import { useCreateAdGroup, useCreatePricingRule } from '@hprofits/api-client';
import type { CampaignGroupId, CreativeGroupId, TargetGroupId } from '@hprofits/shared/types';
import type { SelectOption } from '@hprofits/ui';
import type { SmartFormFieldDef, UseSmartFormDialogFn } from '@hprofits/ui-smart-controls';
import { defineSmartFields, useSmartFormDialog } from '@hprofits/ui-smart-controls';
import { MaybeRefOrGetter, toValue } from 'vue';
import { useRouter } from 'vue-router';
import { z } from 'zod';

import { apiClient } from '@/api-client/client';
import { useIsAdmin } from '@/composables/useIsAdmin';
import { t } from '@/i18n';
import { nextNewName } from '@/modules/campaigns/components/Layout/composables';
import { justCreatedState } from '@/modules/campaigns/composables/useOnJustCreated';
import { getAdGroupCreateMutationErrorMessage } from '@/modules/campaigns/utils/mutation-errors/adGroup';
import { buildPricingRuleCreatePayload } from '@/modules/campaigns/views/AdGroup/utils';
import { useRouteResolverCampaigns } from '@/modules/common/route/useRouteResolverCampaigns';

// Name reuses `CreateAdGroupSchema.name`; ID fields reuse the API's branded
// schemas from `PricingRuleCommonInputSchema` so a contract change at the
// backend surfaces here as a compile error. `onSubmit` receives the branded
// IDs directly — no parse, no cast at the call site.

export const getAdGroupCreateTargetRequiredMessage = (): string => t('modules.campaigns.actions.selectTargetGroup');
export const getAdGroupCreateCreativeRequiredMessage = (): string => t('modules.campaigns.actions.selectCreativeGroup');

const DialogSchema = z.object({
  name: CreateAdGroupSchema.unwrap().shape.name,
  // Piped through a plain number so an unset Select reports the copy above
  // instead of zod's "expected number, received null"; the brand and the range
  // still come from the API schema.
  targetGroupId: z
    .number({ error: () => getAdGroupCreateTargetRequiredMessage() })
    .pipe(PricingRuleCommonInputSchema.unwrap().shape.targetGroupId),
  creativeGroupId: z
    .number({ error: () => getAdGroupCreateCreativeRequiredMessage() })
    .pipe(PricingRuleCommonInputSchema.unwrap().shape.creativeGroupId),
});

type AdGroupCreateForm = z.infer<typeof DialogSchema>;

/**
 * What the dialog holds while open: either Select starts unset unless exactly one
 * option exists. `DialogSchema` rejects the nulls at submit, so `onSubmit` still
 * receives the complete `AdGroupCreateForm`.
 */
type AdGroupCreateDraft = Omit<AdGroupCreateForm, 'targetGroupId' | 'creativeGroupId'> & {
  targetGroupId: TargetGroupId | null;
  creativeGroupId: CreativeGroupId | null;
};

// Static shapes live at module scope; the two Select-typed fields are built per
// call to inject the reactive options. `NAME_FIELD` is fully constant.
//
// NOTE: the new-stack SmartForm (ui-smart-controls) has no `required` concept
// at all, so unlike the legacy dialog, none of these three fields show a label
// asterisk anymore. All three are still enforced by `DialogSchema` at submit —
// the cosmetic hint is gone; `clearable: false` on the selects is now the only
// UI signal that a selection is mandatory.
// Persisted, not display copy: the name reaches the server as-is, so a localized base would leave
// one campaign holding ad groups named in whichever language each was created in, and `nextNewName`
// would dedup "(2)" against a base it no longer recognises after a locale switch.
const AD_GROUP_DEFAULT_NAME = 'New Ad';

const adGroupCreateFields = defineSmartFields<AdGroupCreateDraft>();

const NAME_FIELD: SmartFormFieldDef = adGroupCreateFields.text('name', {
  get label() {
    return t('modules.campaigns.actions.adGroupFields.name');
  },
});

type TargetSelectOption = SelectOption<TargetGroupId>;
type CreativeSelectOption = SelectOption<CreativeGroupId>;

const buildFields = (
  targetOptions: ReadonlyArray<TargetSelectOption>,
  creativeOptions: ReadonlyArray<CreativeSelectOption>,
): ReadonlyArray<SmartFormFieldDef> => [
  NAME_FIELD,
  adGroupCreateFields.select.list('targetGroupId', {
    label: t('modules.campaigns.actions.adGroupFields.targetGroup'),
    options: targetOptions,
    clearable: false,
    highlightWhen: (values) => targetOptions.length > 1 && values.targetGroupId == null,
  }),
  adGroupCreateFields.select.list('creativeGroupId', {
    label: t('modules.campaigns.actions.adGroupFields.creativeGroup'),
    options: creativeOptions,
    clearable: false,
    highlightWhen: (values) => creativeOptions.length > 1 && values.creativeGroupId == null,
  }),
];

export type UseAdGroupCreateDialogDeps = {
  campaignGroupId: MaybeRefOrGetter<CampaignGroupId | null>;
  existingAdNames: MaybeRefOrGetter<ReadonlyArray<string>>;
  targetOptions: MaybeRefOrGetter<ReadonlyArray<TargetSelectOption>>;
  creativeOptions: MaybeRefOrGetter<ReadonlyArray<CreativeSelectOption>>;
};

export type UseAdGroupCreateDialogReturn = {
  open: () => Promise<void>;
};

export const useAdGroupCreateDialog = ({
  campaignGroupId,
  existingAdNames,
  targetOptions,
  creativeOptions,
}: UseAdGroupCreateDialogDeps): UseAdGroupCreateDialogReturn => {
  const router = useRouter();
  const routeResolver = useRouteResolverCampaigns();
  const createAdGroup = useCreateAdGroup(apiClient);
  const createPricingRule = useCreatePricingRule(apiClient);
  const openFormDialog: UseSmartFormDialogFn = useSmartFormDialog();
  const isAdmin = useIsAdmin();

  const open = async (): Promise<void> => {
    const cId = toValue(campaignGroupId);
    if (cId === null) {
      return;
    }

    const currentTargetOptions = toValue(targetOptions);
    const currentCreativeOptions = toValue(creativeOptions);

    // A Select with exactly one available option is pre-filled; with zero or
    // several the draft legitimately starts without the id.
    const defaultValues: AdGroupCreateDraft = {
      name: nextNewName(AD_GROUP_DEFAULT_NAME, toValue(existingAdNames), AD_GROUP_NAME_MAX_LENGTH),
      targetGroupId: currentTargetOptions.length === 1 ? (currentTargetOptions[0]?.value ?? null) : null,
      creativeGroupId: currentCreativeOptions.length === 1 ? (currentCreativeOptions[0]?.value ?? null) : null,
    };

    await openFormDialog<AdGroupCreateForm, AdGroupCreateDraft>({
      title: t('modules.campaigns.actions.newAdGroup'),
      schema: DialogSchema,
      fields: buildFields(currentTargetOptions, currentCreativeOptions),
      defaultValues,
      submitText: t('modules.campaigns.actions.createSubmit'),
      onSubmit: async ({ name, targetGroupId, creativeGroupId }) => {
        try {
          const created = await createAdGroup.mutateAsync({
            campaignId: cId,
            name,
            status: 'enabled',
          });

          const rulePayload: CreatePricingRuleInput = buildPricingRuleCreatePayload({
            adGroupId: created.id,
            campaignId: cId,
            targetGroupId,
            creativeGroupId,
            isAdmin: toValue(isAdmin),
          });

          await createPricingRule.mutateAsync(rulePayload);
          const adGroupRoute = toValue(routeResolver.adGroup(cId, created.id));
          await router.push({
            ...adGroupRoute,
            state: justCreatedState(String(created.id)),
          });
        } catch (err) {
          // Re-throw with a friendly message — the dialog footer swallows the rejection to stay
          // open, so the caller's error handler never sees it and `useSubmitDialog` renders
          // whatever message this Error carries. Same seam as `useAdGroupCopyDialog` (#910).
          throw new Error(getAdGroupCreateMutationErrorMessage(err), { cause: err });
        }
      },
    });
  };

  return { open };
};
